import { useState, useEffect, useMemo } from 'react';
import { css } from '@emotion/css';
import { useParams, useNavigate } from 'react-router-dom';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Box from '@amzn/meridian/box';
import Card from '@amzn/meridian/card';
import Breadcrumb, { BreadcrumbGroup } from '@amzn/meridian/breadcrumb';
import Tab, { TabGroup } from '@amzn/meridian/tab';
import Alert from '@amzn/meridian/alert';
import SearchField from '@amzn/meridian/search-field';
import Responsive from '@amzn/meridian/responsive';
import { useTheme as useMeridianTheme } from '@amzn/meridian/theme';
import arrowLeftIconTokens from '@amzn/meridian-tokens/base/icon/arrow-left-small';
import refreshIconTokens from '@amzn/meridian-tokens/base/icon/refresh';
import sparklesIconTokens from '@amzn/meridian-tokens/base/icon/sparkles-small';
import ActionButton from '../components/ActionButton';
import ApiDetailsList from '../components/ApiDetailsList';
import EndpointsDisclosure from '../components/EndpointsDisclosure';
import StatusBadge from '../components/StatusBadge';
import { useServiceApis } from '../hooks/useServiceApis';
import { describeCatalogStatus, useCatalogJob } from '../hooks/useCatalogJob';
import { ApiMetadata, CatalogJobStatus } from '../api/services';
import CatalogJobFailure from '../components/CatalogJobFailure';
import CatalogJobPollingStatus from '../components/CatalogJobPollingStatus';
import { catalogJobHref } from '../utils/catalog-job-route';
import SchemaViewer from '../components/SchemaViewer';
import InlineProgress from '../components/InlineProgress';
import { SkeletonBlock, SkeletonLine } from '../components/Skeletons';
import useSurfaceStyles from '../hooks/useSurfaceStyles';
import { createTransition, reducedMotionQuery } from '../theme/motion';

type ApiTab = 'tool' | 'details';

const apiNameClass = css({
  display: 'block',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  width: '100%',
});

const tabularNumbersClass = css({
  fontVariantNumeric: 'tabular-nums',
});

// Reserves the status badge's height so the header does not grow when catalog status arrives.
const statusRowClass = css({
  minHeight: 24,
});

function formatApiCount(apiCount: number, hasMore: boolean): string {
  return `${apiCount}${hasMore ? '+' : ''}`;
}

// While API details load, the label reads "12 / 47 APIs" and settles on "47 APIs", so the total
// and noun stay anchored at the end and only the leading progress count changes.
function formatApiCountLabel({
  loadedApiCount,
  matchingApiCount,
  detailsLoadedCount,
  isFiltered,
  isLoadingDetails,
  hasMore,
}: {
  loadedApiCount: number;
  matchingApiCount: number;
  detailsLoadedCount: number;
  isFiltered: boolean;
  isLoadingDetails: boolean;
  hasMore: boolean;
}): string {
  if (isFiltered) return `${matchingApiCount} of ${loadedApiCount}`;
  const noun = loadedApiCount === 1 && !hasMore ? 'API' : 'APIs';
  const totalLabel = `${formatApiCount(loadedApiCount, hasMore)} ${noun}`;
  return isLoadingDetails ? `${detailsLoadedCount} / ${totalLabel}` : totalLabel;
}

function formatTimestamp(timestamp?: number): string {
  if (!timestamp) return '-';
  return new Date(timestamp).toLocaleString();
}

function formatDate(timestamp?: number): string {
  if (!timestamp) return '-';
  return new Date(timestamp).toLocaleDateString();
}

// Single source of truth for which schema the UI renders.
// Priority: AI-improved -> deterministic -> legacy originalSchema.
function resolveSchema(api: ApiMetadata): string | undefined {
  return api.aiImprovedSchema ?? api.deterministicSchema ?? api.originalSchema;
}

// Renders only the input schema (no name/description wrapper), using the
// resolved schema: AI-improved -> deterministic -> legacy originalSchema.
function formatInputSchema(api: ApiMetadata): string {
  const schema = resolveSchema(api);
  if (!schema) return 'No input schema available';
  try {
    return JSON.stringify(JSON.parse(schema), null, 2);
  } catch {
    return schema;
  }
}

function serviceHref(serviceName?: string): string {
  return `/services/${encodeURIComponent(serviceName || '')}`;
}

function formatCommitId(commitId?: string): string | undefined {
  return commitId?.slice(0, 8);
}

function KeyValue({
  label,
  value,
  monospace = false,
  title,
}: {
  label: string;
  value?: string | number | null;
  monospace?: boolean;
  title?: string;
}) {
  return (
    <Column spacing="100">
      <Text type="b100" color="secondary">
        {label}
      </Text>
      <Text type="b200" breakWord fontFamily={monospace ? 'amazonEmberMonospace' : undefined} title={title}>
        {value || '-'}
      </Text>
    </Column>
  );
}

function ApiListItem({ api, selected, onSelect }: { api: ApiMetadata; selected: boolean; onSelect: () => void }) {
  const meridianTheme = useMeridianTheme();
  const { accentTintColor: selectedBackgroundColor } = useSurfaceStyles();
  const apiListItemClass = css({
    position: 'relative',
    display: 'block',
    width: '100%',
    minWidth: 0,
    padding: '8px 12px',
    border: 'none',
    borderRadius: 8,
    background: selected ? selectedBackgroundColor : 'transparent',
    color: selected ? meridianTheme('themeActionPrimaryDefault') : meridianTheme('themeInkDefault'),
    fontFamily: 'inherit',
    fontSize: 14,
    fontWeight: selected ? 600 : 450,
    lineHeight: '20px',
    textAlign: 'left',
    cursor: 'pointer',
    transition: createTransition(['background-color', 'color']),
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 8,
      bottom: 8,
      width: 3,
      borderRadius: 3,
      backgroundColor: meridianTheme('themeActionPrimaryDefault'),
      opacity: selected ? 1 : 0,
      transform: selected ? 'scaleY(1)' : 'scaleY(0.4)',
      transition: createTransition(['opacity', 'transform']),
    },
    '&:hover': {
      background: selected ? selectedBackgroundColor : meridianTheme('themeActionNeutralSelected'),
    },
    '&:focus-visible': {
      outline: `2px solid ${meridianTheme('themeBorderFocus')}`,
      outlineOffset: -2,
    },
    [reducedMotionQuery]: { transition: 'none', '&::before': { transition: 'none' } },
  });

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`${apiListItemClass} ${apiNameClass}`}
      title={api.apiName}
      aria-current={selected ? 'true' : undefined}
      data-api-selected={selected ? 'true' : undefined}
    >
      {api.apiName}
    </button>
  );
}

const API_LIST_SKELETON_WIDTHS = ['72%', '58%', '66%', '48%', '70%', '54%', '62%', '44%'];

// Matches ApiListItem: 8px vertical padding around a 20px line, inside the list's 2px item spacing.
function ApiListSkeleton() {
  const itemClass = css({ padding: '8px 12px' });
  return (
    <div role="status" aria-label="Loading APIs">
      <Column spacing="100">
        {API_LIST_SKELETON_WIDTHS.map((width) => (
          <div key={width} className={itemClass}>
            <SkeletonLine width={width} lineHeight={20} />
          </div>
        ))}
      </Column>
    </div>
  );
}

// Matches the selected-API panel: name (h400, 28px line), description (20px), the tab row, then the
// schema viewer's header and code area, so the panel keeps its height when the first API loads.
function ApiPanelSkeleton() {
  const meridianTheme = useMeridianTheme();
  const tabRowClass = css({
    display: 'flex',
    gap: 24,
    height: 40,
    alignItems: 'center',
    borderBottom: `1px solid ${meridianTheme('themeBorderSubdued')}`,
  });
  return (
    <Column spacing="300" minWidth={0}>
      <Column spacing="100">
        <SkeletonLine width="42%" lineHeight={28} />
        <SkeletonLine width="68%" lineHeight={20} />
      </Column>
      <div className={tabRowClass} aria-hidden="true">
        <SkeletonBlock width={84} height={14} />
        <SkeletonBlock width={52} height={14} />
      </div>
      <SkeletonBlock width="100%" height={320} />
    </Column>
  );
}

function ServiceDetail() {
  const { serviceName } = useParams();
  const navigate = useNavigate();
  const [selectedApiName, setSelectedApiName] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ApiTab>('tool');
  const [apiFilter, setApiFilter] = useState('');

  const {
    apis,
    detailStatusByApiName,
    isLoading,
    isLoadingDetails,
    loadingProgress,
    error,
    hasMore,
    loadMore,
    refresh,
    retryApiDetails,
  } = useServiceApis(serviceName || '');
  const catalogJob = useCatalogJob(serviceName || '', { forceSubmit: true });
  const { serviceMetadata } = catalogJob;

  useEffect(() => {
    if (apis.length === 0) {
      setSelectedApiName(null);
      return;
    }

    if (!selectedApiName || !apis.some((api) => api.apiName === selectedApiName)) {
      setSelectedApiName(apis[0].apiName);
    }
  }, [apis, selectedApiName]);

  const selectedApi = useMemo(
    () => apis.find((api) => api.apiName === selectedApiName) || null,
    [apis, selectedApiName],
  );
  const selectedApiDetailStatus = selectedApiName ? detailStatusByApiName[selectedApiName] : undefined;

  const filteredApis = useMemo(
    () => apis.filter((api) => api.apiName.toLowerCase().includes(apiFilter.toLowerCase())),
    [apis, apiFilter],
  );

  const apiCountLabel = formatApiCountLabel({
    loadedApiCount: apis.length,
    matchingApiCount: filteredApis.length,
    detailsLoadedCount: loadingProgress.loaded,
    isFiltered: Boolean(apiFilter),
    isLoadingDetails: isLoadingDetails && loadingProgress.total > 0,
    hasMore,
  });
  const navigateTo = (href: string) => navigate(href);
  const isCatalogJobRunning = catalogJob.isPolling || catalogJob.catalogStatus === CatalogJobStatus.PENDING;
  const isCatalogJobFailed = catalogJob.catalogStatus === CatalogJobStatus.FAILURE;
  const isRefreshingApis = isLoading || isLoadingDetails;
  const isRegenerationBusy = catalogJob.isSubmitting || isCatalogJobRunning;
  const showCatalogJobPanel = Boolean(catalogJob.submittedJobId) || isCatalogJobRunning || isCatalogJobFailed;

  const isInitialApiLoad = isLoading && apis.length === 0;
  const isInitialMetadataLoad = !serviceMetadata && catalogJob.isLoadingStatus;

  if (error) {
    return (
      <Column spacing="400" width="100%">
        <BreadcrumbGroup>
          <Breadcrumb href="/services" onClick={navigateTo}>
            Services
          </Breadcrumb>
          <Breadcrumb href={serviceHref(serviceName)}>{serviceName || 'Service'}</Breadcrumb>
        </BreadcrumbGroup>
        <Alert type="error" size="large" title="APIs failed to load" titleLevel={1}>
          {error}
        </Alert>
        <Row spacing="300" wrap="down">
          <ActionButton variant="primary" onClick={refresh}>
            Try again
          </ActionButton>
          <ActionButton variant="tertiary" iconTokens={arrowLeftIconTokens} onClick={() => navigate('/services')}>
            Back to services
          </ActionButton>
        </Row>
      </Column>
    );
  }

  if (!isInitialApiLoad && apis.length === 0) {
    return (
      <Column spacing="400" width="100%">
        <BreadcrumbGroup>
          <Breadcrumb href="/services" onClick={navigateTo}>
            Services
          </Breadcrumb>
          <Breadcrumb href={serviceHref(serviceName)}>{serviceName || 'Service'}</Breadcrumb>
        </BreadcrumbGroup>
        <Alert type="neutral" size="large" title="No APIs found" titleLevel={1}>
          No APIs found for service &quot;{serviceName}&quot;.
        </Alert>
        <ActionButton variant="tertiary" iconTokens={arrowLeftIconTokens} onClick={() => navigate('/services')}>
          Back to services
        </ActionButton>
      </Column>
    );
  }

  return (
    <Column spacing="400" width="100%" minWidth={0}>
      <BreadcrumbGroup>
        <Breadcrumb href="/services" onClick={navigateTo}>
          Services
        </Breadcrumb>
        <Breadcrumb href={serviceHref(serviceName)}>{serviceName || 'Service'}</Breadcrumb>
      </BreadcrumbGroup>

      <Row alignmentVertical="bottom" widths={['fill', 'fit']} wrap="down" spacing="300">
        <Column spacing="100">
          <Heading level={1} type="h500">
            {serviceName}
          </Heading>
          {isInitialMetadataLoad ? (
            <SkeletonLine width={220} lineHeight={20} />
          ) : (
            <Text type="b200" color="secondary" breakWord>
              {serviceMetadata?.namespace || 'MCP spec metadata'}
            </Text>
          )}
          <Row spacing="200" alignmentVertical="center" wrap="down" className={statusRowClass}>
            {isInitialMetadataLoad && <SkeletonBlock width={180} height={20} />}
            {catalogJob.catalogStatus && (
              <StatusBadge status={catalogJob.catalogStatus} pendingLabel="Catalog generation in progress" />
            )}
            {serviceMetadata?.modifiedAt && (
              <Text type="b100" color="secondary">
                Last updated {formatTimestamp(serviceMetadata.modifiedAt)}
              </Text>
            )}
          </Row>
        </Column>
        <Row spacing="200" widths="fit" wrap="down">
          <ActionButton
            variant="secondary"
            onClick={refresh}
            iconTokens={refreshIconTokens}
            busyIndicator="spin-icon"
            busy={isRefreshingApis}
            disabled={isRefreshingApis}
          >
            Refresh APIs
          </ActionButton>
          <ActionButton
            variant="primary"
            onClick={catalogJob.submit}
            iconTokens={sparklesIconTokens}
            busy={isRegenerationBusy}
            disabled={isRegenerationBusy}
          >
            {catalogJob.isSubmitting
              ? 'Submitting...'
              : isCatalogJobRunning
                ? 'Regeneration in progress'
                : isCatalogJobFailed
                  ? 'Retry spec generation'
                  : 'Regenerate MCP specs'}
          </ActionButton>
        </Row>
      </Row>

      {showCatalogJobPanel && catalogJob.catalogStatus && (
        <Column type="outline" spacingInset="400" spacing="300">
          <Row spacing="300" alignmentVertical="center" wrap="down" widths={['fit', 'fill']}>
            <StatusBadge status={catalogJob.catalogStatus} />
            <Text type="b200" color="secondary" breakWord>
              {describeCatalogStatus(catalogJob.catalogStatus)}
            </Text>
          </Row>
          <Row spacing="300" alignmentVertical="center" alignmentHorizontal="justify" wrap="down">
            {catalogJob.submittedJobId && (
              <KeyValue label="Current job ID" value={catalogJob.submittedJobId} monospace />
            )}
            <ActionButton variant="secondary" size="small" onClick={() => navigate(catalogJobHref(serviceName))}>
              View job details
            </ActionButton>
          </Row>
          <CatalogJobPollingStatus
            isPolling={catalogJob.isPolling}
            pollCount={catalogJob.pollCount}
            pollLimitReached={catalogJob.pollLimitReached}
            isCheckingJobStatus={catalogJob.isSubmitting}
            onRefreshNow={catalogJob.loadStatus}
            onContinue={catalogJob.startPolling}
            onCheckJobStatus={catalogJob.checkJobStatus}
            onStop={catalogJob.dismissPollLimit}
          />
          {isCatalogJobFailed && (
            <CatalogJobFailure
              catalogJobErrorMessage={catalogJob.catalogJobErrorMessage}
              isSubmitting={catalogJob.isSubmitting}
              onRetry={catalogJob.submit}
            />
          )}
        </Column>
      )}

      {catalogJob.error && (
        <Alert type="error" size="medium">
          {catalogJob.error}
        </Alert>
      )}

      <Card spacingInset="400" width="100%">
        <Column spacing="300" overflowX="hidden">
          <Responsive
            query="min-width"
            fallback="1024px"
            props={{
              widths: {
                default: ['grid-6', 'grid-6', 'grid-6', 'grid-6'],
                '720px': ['grid-3', 'grid-3', 'grid-3', 'grid-3'],
              },
            }}
          >
            {({ widths }: { widths: string[] }) => (
              <Row spacing="400" wrap="down" widths={widths}>
                <KeyValue label="APIs" value={isInitialApiLoad ? undefined : formatApiCount(apis.length, hasMore)} />
                <KeyValue label="Created" value={formatDate(serviceMetadata?.createdAt)} />
                <KeyValue label="Last modified" value={formatDate(serviceMetadata?.modifiedAt)} />
                <KeyValue label="Modified by" value={serviceMetadata?.modifiedBy} />
              </Row>
            )}
          </Responsive>
          <Responsive
            query="min-width"
            fallback="1024px"
            props={{
              widths: {
                default: ['grid-6', 'grid-6', 'grid-6'],
                '720px': ['grid-3', 'grid-3', 'grid-3'],
              },
            }}
          >
            {({ widths }: { widths: string[] }) => (
              <Row spacing="400" wrap="down" widths={widths}>
                <KeyValue
                  label="SpecGen commit"
                  value={formatCommitId(serviceMetadata?.specgenCommitId)}
                  title={serviceMetadata?.specgenCommitId}
                />
                <KeyValue label="SpecGen branch" value={serviceMetadata?.specgenBranch} />
                <KeyValue label="SpecGen last synced" value={formatTimestamp(serviceMetadata?.specgenLastSyncTime)} />
              </Row>
            )}
          </Responsive>
          {isInitialMetadataLoad && <SkeletonLine width={140} lineHeight={45} />}
          {serviceMetadata?.endpoints && serviceMetadata.endpoints.length > 0 && (
            <EndpointsDisclosure endpoints={serviceMetadata.endpoints} />
          )}
        </Column>
      </Card>

      <Responsive
        query="min-width"
        fallback="1024px"
        props={{
          widths: {
            default: ['fill', 'fill'],
            '900px': [260, 'fill'],
          },
        }}
      >
        {({ widths }: { widths: (string | number)[] }) => (
          <Row widths={widths} spacing="300" alignmentVertical="top" wrap="down">
            <Card spacingInset="300" minWidth={0} width="100%">
              <Column spacing="300" minWidth={0}>
                <Row alignmentVertical="center" widths={['fill', 'fit']}>
                  <Heading level={2} type="h300">
                    APIs
                  </Heading>
                  {isInitialApiLoad ? (
                    <SkeletonLine width={44} lineHeight={16} />
                  ) : (
                    <Text type="b100" color="secondary" className={tabularNumbersClass}>
                      {apiCountLabel}
                    </Text>
                  )}
                </Row>
                <Box spacingInset="none 100">
                  <SearchField
                    value={apiFilter}
                    onChange={setApiFilter}
                    onSubmit={() => {}}
                    label="Filter APIs"
                    placeholder="Filter APIs"
                    size="small"
                    width="100%"
                    clearButton
                    onClear={() => setApiFilter('')}
                  />
                </Box>
                <Box spacingInset="none 100">
                  <InlineProgress
                    label="Loading API details"
                    active={isLoadingDetails}
                    completed={loadingProgress.loaded}
                    total={loadingProgress.total}
                  />
                </Box>
                <Column spacing="100" minWidth={0} width="100%" overflowX="hidden">
                  {isInitialApiLoad && <ApiListSkeleton />}
                  {filteredApis.map((api) => (
                    <ApiListItem
                      key={api.apiName}
                      api={api}
                      selected={selectedApiName === api.apiName}
                      onSelect={() => {
                        setSelectedApiName(api.apiName);
                        setActiveTab('tool');
                      }}
                    />
                  ))}
                  {filteredApis.length === 0 && apis.length > 0 && (
                    <Column spacingInset="400" alignmentHorizontal="center">
                      <Text type="b200" color="secondary">
                        No APIs match the filter.
                      </Text>
                    </Column>
                  )}
                </Column>
                {hasMore && (
                  <ActionButton
                    variant="tertiary"
                    size="small"
                    onClick={loadMore}
                    busy={isLoading}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Loading...' : 'Load more APIs'}
                  </ActionButton>
                )}
              </Column>
            </Card>

            <Card spacingInset="300" minWidth={0} width="100%">
              <Column spacing="300" minWidth={0}>
                {isInitialApiLoad || selectedApiDetailStatus?.state === 'loading' ? (
                  <ApiPanelSkeleton />
                ) : selectedApiName && selectedApiDetailStatus?.state === 'failed' ? (
                  <Column spacing="300">
                    <Text tag="h2" type="h400" breakWord>
                      {selectedApiName}
                    </Text>
                    <Alert type="error" size="medium">
                      {selectedApiDetailStatus.message}
                    </Alert>
                    <Row>
                      <ActionButton variant="secondary" onClick={() => retryApiDetails(selectedApiName)}>
                        Try again
                      </ActionButton>
                    </Row>
                  </Column>
                ) : selectedApi ? (
                  <>
                    <Column spacing="100">
                      <Text tag="h2" type="h400" breakWord>
                        {selectedApi.apiName}
                      </Text>
                      <Text type="b200" color="secondary" breakWord>
                        {selectedApi.apiDescription || 'No description'}
                      </Text>
                    </Column>

                    <TabGroup value={activeTab} onChange={setActiveTab} fill="line" aria-label="API detail sections">
                      <Tab value="tool" id="tab-tool" aria-controls="panel-tool">
                        API schema
                      </Tab>
                      <Tab value="details" id="tab-details" aria-controls="panel-details">
                        Details
                      </Tab>
                    </TabGroup>

                    {activeTab === 'tool' && (
                      <Box role="tabpanel" id="panel-tool" aria-labelledby="tab-tool">
                        <SchemaViewer
                          code={formatInputSchema(selectedApi)}
                          fileName={`${selectedApi.apiName}-input-schema.json`}
                        />
                      </Box>
                    )}
                    {activeTab === 'details' && (
                      <Box role="tabpanel" id="panel-details" aria-labelledby="tab-details" width="100%">
                        <ApiDetailsList api={selectedApi} />
                      </Box>
                    )}
                  </>
                ) : (
                  <Column alignmentHorizontal="center" spacingInset="600" spacing="300">
                    <Heading level={2} type="h300">
                      Select an API
                    </Heading>
                    <Text type="b200" color="secondary">
                      Choose an API from the list to inspect its tool definition and metadata.
                    </Text>
                  </Column>
                )}
              </Column>
            </Card>
          </Row>
        )}
      </Responsive>
    </Column>
  );
}

export default ServiceDetail;
